// The MODEL-side half of "Add an incident to chat".
//
// The "Add to chat" action injects an incident VISUALLY — a rich card in the
// message flow (see InjectedIncident.tsx). This module is the counterpart that
// gives the CHAT AGENT the run's context so it can actually answer questions
// about the incident (Burrow plan → "Injecting an incident into chat").
//
// It is deliberately thin: the console does NOT assemble or fetch the trace
// blob. It calls one backend endpoint
// (`POST /v1/persistent-agent-groups/:id/agents/:threadId/attach-incident`)
// which server-side fetches the run structure + per-node summaries (bounded —
// never the full traces) and posts them into the thread's agent context as an
// inbox event. The agent reaches deeper detail on demand via the run-trace MCP
// tools (get_run_structure / get_node_detail / search_run_content).

export interface IIncidentContextAttachment {
  threadId: string;
  runId: string;
}

/**
 * Minimal shape of the ky HTTP client the poster needs — accepting an
 * interface (rather than importing ky's types) keeps this function unit-testable
 * without the React/query stack.
 */
export interface IIncidentContextPoster {
  post(url: string, options: { json: unknown }): { json(): Promise<unknown> };
  delete(url: string): { json(): Promise<unknown> };
}

/**
 * Attach `runId`'s run context to the chat thread `threadId`. Fires the backend
 * attach-incident endpoint, which delivers the run structure + node summaries
 * into the agent's context. Resolves once the context event is persisted; the
 * agent picks it up the next time the operator speaks.
 */
export function attachIncidentContextToThread(
  http: IIncidentContextPoster,
  groupId: string,
  { threadId, runId }: IIncidentContextAttachment,
): Promise<unknown> {
  return http
    .post(`v1/persistent-agent-groups/${groupId}/agents/${threadId}/attach-incident`, {
      json: { runId },
    })
    .json();
}

/**
 * Detach `runId` from the chat thread `threadId`. Deletes the durable
 * incident-attach context event(s) server-side, so the inline card disappears
 * on the next (re)load for every viewer and the run drops out of the agent's
 * context. The console also hides the card optimistically (see the injection
 * context) since the live watch stream doesn't echo the deletion.
 */
export function detachIncidentContextFromThread(
  http: IIncidentContextPoster,
  groupId: string,
  { threadId, runId }: IIncidentContextAttachment,
): Promise<unknown> {
  return http
    .delete(`v1/persistent-agent-groups/${groupId}/agents/${threadId}/attach-incident/${runId}`)
    .json();
}
